/**
 * Drawer (mobile menu): toggle on .no-header__toggle click,
 * open/close #main-drawer, data-drawer-open, body overflow, MENU/CLOSE label.
 */
import type { ElementWithRevealInit } from '../../types';

const OPEN_LAG_MS = 180;
const LINE_GAP_MS = 90;
const LINK_ANIM_MS = 700;

function setRevealDownText(el: Element, text: string): void {
  const glyphs = [...text.replace(/\s+/g, ' ')].map((ch) => (ch === ' ' ? '\u00A0' : ch));
  const rds = el.querySelectorAll(':scope > .rd');
  el.setAttribute('aria-label', text);

  if (rds.length === glyphs.length && rds.length > 0) {
    rds.forEach((rd, i) => {
      const a = rd.querySelector('.char.a');
      const b = rd.querySelector('.char.b');
      if (a) {
        a.textContent = glyphs[i];
        (a as HTMLElement).style.setProperty('--i', String(i));
      }
      if (b) {
        b.textContent = glyphs[i];
        (b as HTMLElement).style.setProperty('--i', String(i));
      }
    });
  } else {
    el.innerHTML = '';
    (el as HTMLElement & ElementWithRevealInit).__rdInit = undefined;
    el.textContent = text;
    if (typeof window.initRevealDown === 'function') {
      window.initRevealDown('.no-header__toggle');
    }
  }
}

function setAllMenuLabels(toggles: NodeListOf<Element>, label: string): void {
  toggles.forEach((btn) => setRevealDownText(btn, label));
}

function setDelays(navLinks: NodeListOf<Element>, baseMs: number): void {
  navLinks.forEach((a, i) =>
    (a as HTMLElement).style.setProperty('--delay', `${(baseMs + i * LINE_GAP_MS) / 1000}s`)
  );
}

export function initDrawer(): void {
  const drawer = document.getElementById('main-drawer');
  if (!drawer) return;
  const drawerEl = drawer;

  const toggles = document.querySelectorAll('.no-header__toggle');
  const navLinks = drawerEl.querySelectorAll('.no-drawer__gnb-link');
  const inner =
    drawerEl.querySelector('.no-drawer__container') || drawerEl.querySelector('.no-drawer__inner');

  let closeEarlyTimer: ReturnType<typeof setTimeout> | null = null;
  let closeFinalTimer: ReturnType<typeof setTimeout> | null = null;

  const clearCloseTimers = (): void => {
    if (closeEarlyTimer) {
      clearTimeout(closeEarlyTimer);
      closeEarlyTimer = null;
    }
    if (closeFinalTimer) {
      clearTimeout(closeFinalTimer);
      closeFinalTimer = null;
    }
  };

  function openDrawer(btn?: Element): void {
    clearCloseTimers();
    drawerEl.classList.remove('closing');
    setDelays(navLinks, OPEN_LAG_MS);
    drawerEl.classList.add('active');
    document.documentElement.setAttribute('data-drawer-open', 'true');
    document.body.style.overflow = 'hidden';
    setAllMenuLabels(toggles, 'CLOSE');
    if (btn) {
      btn.setAttribute('aria-expanded', 'true');
      btn.setAttribute('aria-controls', 'main-drawer');
    }
  }

  function closeDrawer(btn?: Element): void {
    if (!drawerEl.classList.contains('active')) return;
    clearCloseTimers();
    setDelays(navLinks, 0);
    drawerEl.classList.add('closing');
    drawerEl.classList.remove('active');

    const wait = LINK_ANIM_MS + (navLinks.length - 1) * LINE_GAP_MS + 60;
    const early = Math.max(0, Math.floor(wait * 0.5));

    closeEarlyTimer = setTimeout(() => {
      document.documentElement.removeAttribute('data-drawer-open');
      setAllMenuLabels(toggles, 'MENU');

      const heroSection = document.querySelector('.no-main-hero');
      if (heroSection) {
        const rect = heroSection.getBoundingClientRect();
        const inView = rect.top <= window.innerHeight && rect.bottom >= 0;
        if (inView) {
          document.documentElement.setAttribute('data-drawer-open', 'other');
        }
      }
    }, early);

    closeFinalTimer = setTimeout(() => {
      drawerEl.classList.remove('closing');
      document.body.style.overflow = '';
      if (btn) btn.setAttribute('aria-expanded', 'false');
    }, wait);
  }

  const isOpen = (): boolean => drawerEl.classList.contains('active');

  toggles.forEach((btn) => {
    btn.addEventListener('click', () => {
      if (isOpen()) closeDrawer(btn);
      else openDrawer(btn);
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen()) closeDrawer();
  });

  drawerEl.addEventListener('click', (e) => {
    if (inner && !inner.contains(e.target as Node)) closeDrawer();
  });

  drawerEl.querySelectorAll('a').forEach((a) => {
    a.addEventListener('click', () => closeDrawer());
  });
}
